import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Commit a route before doing its first heavy render.
 *
 * React Router renders a navigation inside a transition. The OUTGOING page
 * stays mounted and subscribed until that transition commits, and every live
 * store update it receives is urgent work that interrupts and restarts the
 * incoming page's render. Under a busy feed the transition is starved until
 * React expires it (~5 s): the old page stays on screen, still updating, and
 * the click looks ignored. Measured twice — dashboard → Tracker (#990,
 * docs/260926TrackerNavigationEntry.md) and Tracker → Game Detail (#1407,
 * docs/261031TrackerToGameDetailEntry.md).
 *
 * This boundary makes the transition's work trivial: it commits a labelled
 * loading view, so the outgoing page unmounts and releases its subscriptions,
 * then mounts `children` on the next animation frame.
 *
 * Invariants:
 *  - runs once per route ENTRY. Param changes inside the same route element
 *    (game A → game B) reuse the mounted children and never show the loader;
 *    account/feed updates never remount them.
 *  - a navigation that supersedes the entry cancels the frame, so an
 *    abandoned visit does no page work. A hidden tab mounts on its next
 *    visible frame.
 *  - it never delays or reorders feed/account events, and never touches
 *    route history, targets or drafts.
 */
export function RouteEntry({ label, children }: { label: string; children: ReactNode }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (!entered) {
    return (
      <div role="status" className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        Loading {label}…
      </div>
    );
  }
  return children;
}
